"use client";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, ChevronDown, Loader2 } from "lucide-react";
import {
  calculatePlanReturns,
  formatPlanRateSummary,
  parsePlanMoney,
  periodInterestFieldLabel,
} from '@/lib/planReturns';

/**
 * Stake Calculator — fully dynamic.
 *
 * Plans are fetched from `/api/plans` (same source as the Plans section above)
 * and every value below reacts to the selected plan: slider range, step,
 * monthly rate, simple-vs-compound math, and the projected returns panel.
 *
 * Cross-component hand-off:
 *  - The Plans cards' "Calculate Returns" button writes the plan title to
 *    `sessionStorage` AND dispatches a `stake-calc-select` window event.
 *  - This component listens for the event AND reads sessionStorage on mount,
 *    so a click pre-selects the matching plan whether the calculator was
 *    already mounted or not (single-page nav + first paint both covered).
 */

const HEX_RE = /^#([0-9a-f]{3}){1,2}$/i;
const FALLBACK_COLORS = ["#F59E0B", "#EA580C", "#DC2626", "#0F52BA", "#10B981"];

function safeAccent(value, fallback) {
  return typeof value === "string" && HEX_RE.test(value) ? value : fallback;
}

function fmt(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function buildPlanConfig(plan, idx) {
  const min = parsePlanMoney(plan?.minPrincipal);
  const rawMax = parsePlanMoney(plan?.maxPrincipal);
  const rateSummary = formatPlanRateSummary(plan);
  return {
    key: String(plan?.name || `plan-${idx}`).toLowerCase(),
    name: String(plan?.name || `Plan ${idx + 1}`),
    color: safeAccent(plan?.accentColor, FALLBACK_COLORS[idx % FALLBACK_COLORS.length]),
    min,
    max: rawMax > min ? rawMax : Math.max(min * 10, min + 100000),
    monthsLabel: rateSummary.durationLabel,
    rateSummary: rateSummary.summary,
    raw: plan,
  };
}

function buildSliderStep(min, max) {
  const range = Math.max(1, max - min);
  return Math.max(100, Math.round(range / 1000));
}

export default function StakeCalculator() {
  const [plansRaw, setPlansRaw] = useState(null);
  const [selectedKey, setSelectedKey] = useState("");
  const [wantedName, setWantedName] = useState("");
  const [deposit, setDeposit] = useState(0);
  const [open, setOpen] = useState(false);
  const [inputDraft, setInputDraft] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/plans");
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setPlansRaw(Array.isArray(data?.plans) ? data.plans : []);
      } catch {
        if (!cancelled) setPlansRaw([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("stake-calc-plan");
      if (stored) {
        setWantedName(stored);
        sessionStorage.removeItem("stake-calc-plan");
      }
    } catch {
      /* sessionStorage may be blocked — safe to ignore */
    }
    const onSelect = (e) => {
      const v = typeof e?.detail === "string" ? e.detail : "";
      if (v) setWantedName(v);
    };
    window.addEventListener("stake-calc-select", onSelect);
    return () => window.removeEventListener("stake-calc-select", onSelect);
  }, []);

  const plans = useMemo(
    () => (plansRaw ? plansRaw.map(buildPlanConfig) : []),
    [plansRaw]
  );

  useEffect(() => {
    if (!plans.length) return;
    if (wantedName) {
      const target = plans.find(
        (p) => p.name.toLowerCase() === wantedName.toLowerCase()
      );
      if (target) {
        setSelectedKey(target.key);
        setWantedName("");
        return;
      }
    }
    if (!selectedKey || !plans.find((p) => p.key === selectedKey)) {
      setSelectedKey(plans[0].key);
    }
  }, [plans, wantedName, selectedKey]);

  const plan = plans.find((p) => p.key === selectedKey) || null;

  useEffect(() => {
    if (!plan) return;
    setDeposit((prev) => {
      const cur = Number(prev) || 0;
      const fallback = Math.min(plan.max, Math.max(plan.min, plan.min || 5000));
      if (cur < plan.min || cur > plan.max) return fallback;
      return cur;
    });
  }, [plan]);

  useEffect(() => {
    setInputDraft(String(deposit || ""));
  }, [deposit]);

  const results = useMemo(() => {
    if (!plan?.raw) {
      return {
        periodInterest: 0,
        totalInterest: 0,
        totalWithdrawal: 0,
        periodLabel: 'month',
      };
    }
    return calculatePlanReturns(deposit, plan.raw);
  }, [deposit, plan]);

  const loading = plansRaw === null;
  const empty = !loading && plans.length === 0;

  const accentColor = plan?.color || "#F59E0B";
  const sliderStep = plan ? buildSliderStep(plan.min, plan.max) : 100;
  const sliderProgress = plan
    ? plan.max > plan.min
      ? ((Number(deposit) - plan.min) / (plan.max - plan.min)) * 100
      : 0
    : 0;

  return (
    <section
      id="calculator"
      className="bg-bg-secondary 2xl:px-6 xl:px-5 lg:px-4 md:px-4  px-4 2xl:py-24 xl:py-20 lg:py-16 md:py-12 py-10"
    >
      <div className="mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14 text-center"
        >
          <span className="mb-4 inline-block rounded-full border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.1)] px-4 py-1.5 text-xs font-medium text-accent-amber sm:text-sm">
            Stake Calculator
          </span>
          <h2 className="font-display mb-3 text-[clamp(32px,5vw,52px)]">
            Calculate Your <span className="text-ember">Returns</span>
          </h2>
          <p className="text-base text-text-muted">
            Simulate your earnings before committing to any plan.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="card-base p-4 2xl:p-8 xl:p-7 lg:p-6 md:p-5 sm:p-4"
        >
          {loading ? (
            <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 text-text-muted">
              <Loader2 className="h-7 w-7 animate-spin text-accent-amber" aria-hidden />
              <p className="text-sm">Loading plans…</p>
            </div>
          ) : empty ? (
            <div className="flex min-h-[280px] flex-col items-center justify-center gap-2 text-text-muted">
              <Calculator className="h-7 w-7 text-accent-amber" aria-hidden />
              <p className="text-sm">No investment plans are available right now.</p>
            </div>
          ) : (
            <div className="grid items-start gap-10 lg:grid-cols-2">
              <div>
                <div className="mb-8">
                  <label className="mb-2.5 block text-xs font-semibold uppercase tracking-[0.06em] text-text-muted">
                    Select Plan
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setOpen((o) => !o)}
                      className="flex w-full cursor-pointer items-center justify-between rounded-xl border border-border bg-bg-secondary px-4 py-3.5 text-[15px] font-semibold text-text-primary"
                    >
                      <span className="flex items-center gap-2.5">
                        <span
                          style={{ background: accentColor }}
                          className="inline-block h-2.5 w-2.5 rounded-full"
                        />
                        {plan?.name || "Choose a plan"}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${open ? "rotate-180" : "rotate-0"}`}
                      />
                    </button>
                    {open && (
                      <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-10 overflow-hidden rounded-xl border border-border bg-bg-card shadow-panel-card">
                        {plans.map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => {
                              setSelectedKey(p.key);
                              setOpen(false);
                            }}
                            style={{
                              background:
                                p.key === selectedKey
                                  ? "rgba(245,158,11,0.12)"
                                  : "transparent",
                            }}
                            className="flex w-full cursor-pointer items-center gap-2.5 px-4 py-3 text-left text-sm text-text-primary hover:bg-bg-secondary"
                          >
                            <span
                              style={{ background: p.color }}
                              className="inline-block h-2.5 w-2.5 rounded-full"
                            />
                            {p.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="mb-2.5 flex items-center justify-between">
                    <label className="text-xs font-semibold uppercase tracking-[0.06em] text-text-muted">
                      Deposit Amount
                    </label>
                    <span
                      className="font-display text-xl font-extrabold"
                      style={{ color: accentColor }}
                    >
                      {fmt(deposit)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={plan?.min ?? 0}
                    max={plan?.max ?? 100000}
                    step={sliderStep}
                    value={deposit}
                    onChange={(e) => setDeposit(Number(e.target.value))}
                    className="h-1.5 w-full cursor-pointer appearance-none rounded-md"
                    style={{
                      background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${sliderProgress}%, var(--border) ${sliderProgress}%, var(--border) 100%)`,
                    }}
                  />
                  <div className="mt-2 flex justify-between text-xs text-text-muted">
                    <span>{plan ? fmt(plan.min) : "—"}</span>
                    <span>{plan ? fmt(plan.max) : "—"}</span>
                  </div>

                  <input
                    type="text"
                    inputMode="numeric"
                    value={inputDraft}
                    onChange={(e) => setInputDraft(e.target.value.replace(/[^0-9.]/g, ""))}
                    onBlur={() => {
                      if (!plan) return;
                      const parsed = Number(inputDraft);
                      const safe = Number.isFinite(parsed) ? parsed : plan.min;
                      setDeposit(Math.max(plan.min, Math.min(plan.max, safe)));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                    className="mt-4 w-full rounded-lg border border-border bg-bg-secondary px-4 py-3 text-[15px] text-text-primary outline-none transition focus:border-accent-amber"
                  />

                  <div
                    style={{
                      background: `${accentColor}0d`,
                      border: `1px solid ${accentColor}25`,
                      borderRadius: "8px",
                    }}
                    className="mt-5 rounded-lg p-3.5 text-sm text-text-muted"
                  >
                    <span style={{ color: accentColor, fontWeight: 600 }}>
                      {plan?.name}
                    </span>
                    {" · "}
                    {plan?.monthsLabel} · {plan?.rateSummary}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="mb-2 flex items-center gap-2.5">
                  <Calculator size={20} color={accentColor} />
                  <span className="font-display text-base font-bold">
                    Projected Returns
                  </span>
                </div>

                {[
                  {
                    label: periodInterestFieldLabel(results.periodLabel),
                    value: results.periodInterest,
                    highlight: false,
                  },
                  {
                    label: "Total Interest Earned",
                    value: results.totalInterest,
                    highlight: false,
                  },
                  {
                    label: "Total Withdrawal",
                    value: results.totalWithdrawal,
                    highlight: true,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    style={{
                      background: item.highlight
                        ? `linear-gradient(135deg, ${accentColor}18, ${accentColor}06)`
                        : "var(--bg-secondary)",
                      border: `1px solid ${item.highlight ? accentColor + "35" : "var(--border)"}`,
                    }}
                    className="rounded-xl p-4 2xl:p-6 xl:p-6 lg:p-5 md:p-5 sm:p-4"
                  >
                    <div className="mb-2 text-xs uppercase tracking-[0.06em] text-text-muted">
                      {item.label}
                    </div>
                    <div
                      style={{
                        fontSize: item.highlight ? "30px" : "24px",
                        color: item.highlight ? accentColor : "var(--text-primary)",
                      }}
                      className="font-display font-extrabold"
                    >
                      {fmt(item.value)}
                    </div>
                  </div>
                ))}

                <p className="mt-1 text-xs leading-relaxed text-text-muted">
                  * Daily interest on principal over the plan term. Subscribe Mon–Fri (your local
                  time). Wallet withdrawals are fee-free anytime. Estimates only — actual returns may
                  vary.
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
